import { createSlice } from "@reduxjs/toolkit";
import moment from 'moment';

const initialState = {
    // Fila de criterio de la sesion. El modelo y el area salen del detalle del
    // plan de terapia (la flecha hacia abajo del listado de areas del plan):
    //   - el area es OBLIGATORIA;
    //   - el objetivo y el criterio son opcionales, segun los indicadores
    //     DetObjetivo y DetCriterio del plan.
    defaultEntity: [{
        id: 0,
        numero: 0,
        idAreaObjetivoCriterio: 0,
        // El modelo viene del plan: es de solo lectura y es el filtro padre de
        // las areas.
        idModelo: 0,
        codigoModelo: "",
        modelo: "",
        idArea: 0,
        codigoArea: "",
        area: "",
        areaModelo: [],
        idAreaObjetivo: 0,
        codigoObjetivo: "",
        objetivo: "",
        ordenObjetivo: 0,
        preguntaObjetivo: "",
        ejemploObjetivo: "",
        valorCriterio: 0,
        criterio: "",
        ordenCriterio: 0,
        idPuntuacionGrupo: 1,
        detalle: "",
        fechaRegistro: null,
        usuarioRegistro: null,
        fechaModificacion: null,
        usuarioModificacion: null,
        areaObjetivo: [],
        areaObjetivoCriterio: [],
        // Indicadores heredados del plan: false oculta el campo en la fila.
        detObjetivo: true,
        detCriterio: true,
        // Escala de avance del plan: define los grupos de puntuacion que
        // puede elegir el terapeuta en esta fila.
        idPuntuacion: 0,
        puntuacionGrupo: "",
        gruposPuntuacion: [],
    }],
    // La sesion nueva arranca SIN filas: la fila por defecto (defaultEntity)
    // no se carga, se agrega con el boton de agregar o desde el plan.
    entity: [],
    listModelo: [{id: 0, codigo: "", nombre: "", descripcion: ""}],
    listArea: [{id: 0, codigo: "", nombre: "", descripcion: ""}],
    listPuntuacionGrupo: [{id: 0, descripcion: ""}],
    validationActive: {
      AtLeastOneRow: false,
    },
    validationMessage: [],
}

export const sessionCriterionSlice = createSlice({
    name: "sessionCriterion",
    initialState,
    reducers: {
      setEntity: (state, action) => {
        // La vista no trae los indicadores del plan: si no vienen se asume que
        // se detallan objetivo y criterio, que es el comportamiento previo.
        state.entity = (action.payload || []).map((row) => ({
          ...state.defaultEntity[0],
          ...row,
          detObjetivo: row.detObjetivo !== false,
          detCriterio: row.detCriterio !== false,
        }));

        // Las listas de objetivos y criterios NO se tocan aqui: se piden del
        // servidor al cargar. Antes se ponian arreglos de un solo elemento y el
        // autocompletado solo mostraba el valor ya elegido.
      },
      setData: (state, action) => {
        var { numero, name, value } = action.payload;

        if (name.startsWith("id") || name.startsWith("orden") || name.startsWith("valor")) value = parseInt(value)

        const index = state.entity.findIndex(item => item.numero === numero);
        state.entity[index][name] = value;

        Object.keys(state.validationActive).forEach(function (key) {
            state.validationActive[key] = true;
        })
      },
      add: (state, action) => {
        var maxNumero = state.entity.length === 0 ? 0 : Math.max.apply(null, state.entity.map((item) => { return item.numero; }))

        if (state.entity.length > 0){
          var anterior = state.entity[state.entity.length - 1];

          // El detalle nuevo arranca con el modelo, el area y las listas del
          // detalle anterior: asi se encadenan varios criterios del mismo modelo.
          state.entity.push(state.defaultEntity.map((item) => {
            return {
              ...item,
              numero: maxNumero + 1,
              idModelo: anterior.idModelo,
              codigoModelo: anterior.codigoModelo,
              modelo: anterior.modelo,
              idArea: anterior.idArea,
              codigoArea: anterior.codigoArea,
              area: anterior.area,
              areaModelo: anterior.areaModelo,
              areaObjetivo: anterior.areaObjetivo,
            }
          })[0]);
        }
        else {
          state.entity.push(state.defaultEntity.map((item) => { return { ...item, numero: maxNumero + 1 } })[0]);
        }
      },
      // Agrega un criterio con el modelo y el area de una fila del detalle del
      // plan de terapia (la flecha hacia abajo del listado de areas del plan).
      // Si la fila no tiene area, el criterio entra en blanco para que el
      // terapeuta elija el area.
      addDesdePlan: (state, action) => {
        const detalle = action.payload || {};
        var maxNumero = state.entity.length === 0 ? 0
          : Math.max.apply(null, state.entity.map((item) => item.numero));

        state.entity.push({
          ...state.defaultEntity[0],
          numero: maxNumero + 1,
          idModelo: detalle.idModelo || 0,
          codigoModelo: detalle.codigoModelo || "",
          modelo: detalle.modelo || "",
          idArea: detalle.idArea || 0,
          codigoArea: detalle.codigoArea || "",
          area: detalle.area || "",
          // Filtro inicial del autocompletado de Area: las areas del modelo.
          areaModelo: detalle.areaModelo || [],
          areaObjetivo: [],
          areaObjetivoCriterio: [],
          detObjetivo: detalle.detObjetivo !== false,
          detCriterio: detalle.detCriterio !== false,
          // Escala de avance del plan: de aqui salen los grupos de puntuacion.
          idPuntuacion: detalle.idPuntuacion || 0,
          gruposPuntuacion: [],
        });
      },
      // Los grupos de puntuacion de una fila salen de la escala de avance del
      // plan del area. Se piden por fila porque cada area del plan puede venir
      // de un plan con escala distinta.
      setGruposPuntuacionFila: (state, action) => {
        var { numero, grupos } = action.payload;

        const index = state.entity.findIndex(item => item.numero === numero);
        if (index >= 0) state.entity[index].gruposPuntuacion = grupos || [];
      },
      remove: (state, action) => {
        state.entity = state.entity.filter(item => item.numero !== action.payload)
      },
      setListModelo: (state, action) => {
        state.listModelo = action.payload;
      },
      setListArea: (state, action) => {
        state.listArea = action.payload;
      },
      setAreaModelo: (state, action) => {
        var { numero, areas } = action.payload;
        const index = state.entity.findIndex(item => item.numero === numero);
        if (index >= 0) state.entity[index].areaModelo = areas;
      },
      setListAreaObjetivo: (state, action) => {
        state.listAreaObjetivo = action.payload;
      },
      setListAreaObjetivoCriterio: (state, action) => {
        state.listAreaObjetivoCriterio = action.payload;
      },
      setListGrupoPuntuacion: (state, action) => {
        state.listPuntuacionGrupo = action.payload;
      },
      setValidations: (state, action) => {
        state.validationMessage = action.payload;
      },
      activeValidations: (state, action) => {
        Object.keys(state.validationActive).forEach(function (key) {
            state.validationActive[key] = true;
        })
      },
    },
  });
  
  export const { setEntity, setData, add, addDesdePlan, setGruposPuntuacionFila, remove
    , setIdTerapia, setListModelo, setListArea, setAreaModelo, setListAreaObjetivo, setListAreaObjetivoCriterio, setListGrupoPuntuacion
    , setValidations, activeValidations } = sessionCriterionSlice.actions;
  export default sessionCriterionSlice.reducer;
  
